import { z } from "zod";
import { slugString } from "./slug.js";

// A user's pinned / favourite exercise — a tiny per-user row that references
// a catalog slug only (NO name snapshot; the display name resolves at render
// against the live catalog, [[feedback_no_denormalized_display_snapshots]]).
// Drives the ⭐ pins that float to the top of the Add-Exercise "Exercises"
// tab.
//
// Stored pk=USER#<sub>, sk=BOOKMARK#<exerciseSlug> — ONE row per exercise.
// Keying by the slug (not a uuid) makes a re-pin idempotent (conditional put
// no-ops or overwrites the same key) and an unpin a direct-key delete with no
// scan. There's nothing time-series about a favourite, so no ms component.

const ExerciseSlug = slugString(64);

export const Bookmark = z.object({
  // Partition-key data, included so a single-row read is self-describing.
  // Always equals the row's pk USER#<sub>.
  userSub: z.string().min(1).max(128),
  exerciseSlug: ExerciseSlug,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Bookmark = z.infer<typeof Bookmark>;

// Client → server payload. The slug IS the identity (sk), so there's no id to
// mint; the server fills userSub + timestamps.
export const NewBookmark = Bookmark.omit({
  userSub: true,
  createdAt: true,
  updatedAt: true,
});
export type NewBookmark = z.infer<typeof NewBookmark>;

// ─── Favorites — the Bookmark pattern generalized by kind (HR-S3d) ────────
//
// A user's ⭐ on a program or single-workout plan, stored as its own
// additive row family sk=FAVORITE#<kind>#<itemId> — the legacy exercise
// rows (sk=BOOKMARK#<slug>) are untouched, so the picker's pins keep
// working with zero migration. Same idempotency contract as Bookmark:
// (kind,itemId) IS the identity, re-star overwrites, un-star is a
// direct-key delete. References the item by id only (no name snapshot —
// the display name resolves at render, [[feedback_no_denormalized_display_snapshots]]).
// Templates become favoritable when PE-8 turns them into real Program rows.

export const FavoriteKind = z.enum(["program", "plan"]);
export type FavoriteKind = z.infer<typeof FavoriteKind>;

export const Favorite = z.object({
  userSub: z.string().min(1).max(128),
  kind: FavoriteKind,
  itemId: z.string().min(1).max(64),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Favorite = z.infer<typeof Favorite>;

export const NewFavorite = Favorite.omit({
  userSub: true,
  createdAt: true,
  updatedAt: true,
});
export type NewFavorite = z.infer<typeof NewFavorite>;
